import axios from 'axios';

class ApiService {
  constructor() {
    this.client = null;
    this.baseURL = null;
  }

  initialize(baseURL, getAuthToken) {
    this.baseURL = baseURL;
    this.getAuthToken = getAuthToken;
    
    this.client = axios.create({
      baseURL: baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for authentication
    this.client.interceptors.request.use(
      async (config) => {
        if (this.getAuthToken) {
          const token = await this.getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.error('Unauthorized access - redirecting to login');
        }
        return Promise.reject(error);
      }
    );
  }

  // Jobs API - GET /prod/jobs
  async getJobs() {
    try {
      const response = await this.client.get('/prod/jobs');
      return response.data; // Returns array of job objects directly
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch jobs');
    }
  }

  // Job Creation API - POST /prod/jobs
  async createJob(jobData) {
    try {
      // Format data according to Lambda function expectations
      const payload = {
        title: jobData.title,
        description: jobData.description,
        department: jobData.department,
        location: jobData.location || 'Remote',
        type: jobData.jobType || 'Full-time',
        salary: jobData.salaryRange || '',
        experience: jobData.experienceLevel || '',
        status: jobData.status || 'active',
        requirements: Array.isArray(jobData.requirements) 
          ? jobData.requirements 
          : (jobData.requirements ? [jobData.requirements] : []),
        responsibilities: Array.isArray(jobData.responsibilities) 
          ? jobData.responsibilities 
          : (jobData.responsibilities ? [jobData.responsibilities] : []),
        skills: jobData.skills || [],
        created_by: 'frontend-user',
        priority: 'normal',
        remote_work: jobData.location?.toLowerCase().includes('remote') || false
      };

      const response = await this.client.post('/prod/jobs', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating job:', error);
      throw new Error(error.response?.data?.error || 'Failed to create job');
    }
  }

  // AI Matching API - POST /prod/matches
  async analyzeCandidateMatch(jobId, resumeContent, candidateName = 'Unknown') {
    try {
      const payload = {
        jobId: jobId,
        resumeContent: resumeContent,
        candidateName: candidateName
      };

      const response = await this.client.post('/prod/matches', payload);
      return response.data;
    } catch (error) {
      console.error('Error analyzing candidate match:', error);
      throw new Error(error.response?.data?.error || 'Failed to analyze candidate match');
    }
  }

  // Get all candidate matches for a job from DynamoDB
  async getCandidateMatches(jobId) {
    try {
      const payload = {
        jobId: jobId
      };

      const response = await this.client.post('/prod/matches', payload);
      return response.data;
    } catch (error) {
      console.error('Error getting candidate matches:', error);
      throw new Error(error.response?.data?.error || 'Failed to get candidate matches');
    }
  }

  // Resume Management APIs - Updated for proper S3 backend
  
  // Upload resume to S3 via API Gateway
  async uploadResumeToJob(jobId, file) {
    try {
      const candidateName = file.name.replace(/\.[^/.]+$/, "");
      
      // Convert file to base64 for API transmission
      const fileContent = await this.fileToBase64(file);
      
      // For text files, also extract text content
      let resumeText = '';
      if (file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')) {
        resumeText = await this.readFileAsText(file);
      }
      
      const payload = {
        jobId: jobId,
        fileName: file.name,
        fileContent: fileContent,
        candidateName: candidateName,
        resumeText: resumeText // For text files
      };

      const response = await this.client.post('/prod/resumes', payload);
      
      return {
        success: true,
        resumeId: response.data.resumeId,
        fileName: file.name,
        candidateName: candidateName,
        uploadedAt: response.data.uploadedAt,
        extractedData: response.data.extractedData
      };
    } catch (error) {
      console.error('Error uploading resume:', error);
      throw new Error(`Failed to upload ${file.name}: ${error.response?.data?.error || error.message}`);
    }
  }

  // Get all resumes for a specific job from S3
  async getResumesForJob(jobId) {
    try {
      const response = await this.client.get(`/prod/resumes/${jobId}`);
      
      return response.data.resumes.map(resume => ({
        id: resume.resume_id,
        fileName: resume.file_name,
        candidateName: resume.candidate_name,
        uploadedAt: resume.uploaded_at,
        size: resume.file_size,
        extractedData: resume.extracted_data
      }));
    } catch (error) {
      console.error('Error getting resumes for job:', error);
      throw new Error('Failed to retrieve resumes');
    }
  }

  // Process all existing resumes for a job (Find Candidates functionality)
  async findCandidatesForJob(jobId) {
    try {
      // Get all resumes for this job from S3
      const resumes = await this.getResumesForJob(jobId);
      
      if (resumes.length === 0) {
        return {
          jobId: jobId,
          totalCandidates: 0,
          candidates: [],
          message: 'No resumes found for this job. Please upload resumes first.'
        };
      }

      const results = [];
      
      // Process each resume with AI analysis using existing AI matching endpoint
      for (const resume of resumes) {
        try {
          // Use the extracted data if available, otherwise use raw text
          const resumeContent = resume.extractedData?.raw_text || 
                               this.formatExtractedDataAsText(resume.extractedData) ||
                               'Resume content not available';
          
          // Analyze with AI using existing endpoint
          const analysis = await this.analyzeCandidateMatch(
            jobId, 
            resumeContent, 
            resume.candidateName
          );
          
          results.push({
            fileName: resume.fileName,
            candidateName: resume.candidateName,
            uploadedAt: resume.uploadedAt,
            analysis: analysis,
            matchScore: analysis.analysis?.overall_score || 0,
            extractedData: resume.extractedData
          });
          
        } catch (fileError) {
          console.error(`Error processing resume ${resume.fileName}:`, fileError);
          results.push({
            fileName: resume.fileName,
            candidateName: resume.candidateName,
            error: fileError.message,
            matchScore: 0
          });
        }
      }
      
      // Sort by match score (highest first)
      results.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
      
      return {
        jobId: jobId,
        totalCandidates: results.length,
        processedCandidates: results.filter(r => !r.error).length,
        candidates: results.map((result, index) => ({
          id: `candidate-${index}`,
          name: result.candidateName,
          fileName: result.fileName,
          uploadedAt: result.uploadedAt,
          matchScore: result.matchScore,
          analysis: result.analysis?.analysis,
          error: result.error,
          skills: result.extractedData?.skills || this.extractSkillsFromAnalysis(result.analysis?.analysis),
          experience: result.extractedData?.experience_years ? 
                     `${result.extractedData.experience_years} years` : 
                     this.extractExperienceFromAnalysis(result.analysis?.analysis),
          summary: result.extractedData?.summary || this.extractSummaryFromAnalysis(result.analysis?.analysis),
          email: result.extractedData?.email,
          phone: result.extractedData?.phone,
          education: result.extractedData?.education,
          certifications: result.extractedData?.certifications
        }))
      };
    } catch (error) {
      console.error('Error finding candidates for job:', error);
      throw new Error('Failed to find candidates');
    }
  }

  // Helper method to convert file to base64
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:type;base64, prefix
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  // Helper method to format extracted data as readable text
  formatExtractedDataAsText(extractedData) {
    if (!extractedData || extractedData.error) return '';
    
    let text = `Name: ${extractedData.candidate_name || 'Unknown'}\n`;
    
    if (extractedData.email) text += `Email: ${extractedData.email}\n`;
    if (extractedData.phone) text += `Phone: ${extractedData.phone}\n`;
    if (extractedData.experience_years) text += `Experience: ${extractedData.experience_years} years\n`;
    
    if (extractedData.summary) text += `\nSummary: ${extractedData.summary}\n`;
    
    if (extractedData.skills && extractedData.skills.length > 0) {
      text += `\nSkills: ${extractedData.skills.join(', ')}\n`;
    }
    
    if (extractedData.education && extractedData.education.length > 0) {
      text += `\nEducation: ${extractedData.education.join(', ')}\n`;
    }
    
    if (extractedData.work_experience && extractedData.work_experience.length > 0) {
      text += '\nWork Experience:\n';
      extractedData.work_experience.forEach(exp => {
        text += `- ${exp.position} at ${exp.company} (${exp.duration})\n`;
        if (exp.description) text += `  ${exp.description}\n`;
      });
    }
    
    return text;
  }

  // Helper method to read file content
  async readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  // Helper methods to extract data from AI analysis
  extractSkillsFromAnalysis(analysis) {
    if (!analysis) return [];
    
    // Try to extract skills from various parts of the analysis
    const skills = [];
    
    if (analysis.skills_match?.analysis) {
      // Extract mentioned technologies/skills from the analysis text
      const skillsText = analysis.skills_match.analysis;
      const commonSkills = ['JavaScript', 'React', 'Node.js', 'Python', 'Java', 'AWS', 'Docker', 'Kubernetes'];
      commonSkills.forEach(skill => {
        if (skillsText.toLowerCase().includes(skill.toLowerCase())) {
          skills.push(skill);
        }
      });
    }
    
    return skills;
  }

  extractExperienceFromAnalysis(analysis) {
    if (!analysis?.experience_match?.analysis) return '';
    
    const expText = analysis.experience_match.analysis;
    // Extract years of experience mentioned in the analysis
    const yearMatch = expText.match(/(\d+)\s*years?/i);
    return yearMatch ? `${yearMatch[1]} years of experience` : 'Experience level mentioned in analysis';
  }

  extractSummaryFromAnalysis(analysis) {
    if (!analysis) return '';
    
    // Create a summary from the recommendation and strengths
    let summary = '';
    
    if (analysis.recommendation?.reasoning) {
      summary = analysis.recommendation.reasoning;
    } else if (analysis.strengths && analysis.strengths.length > 0) {
      summary = analysis.strengths.join('. ');
    }
    
    return summary;
  }

  // Interview Questions API - POST /prod/interview-questions
  async generateInterviewQuestions(jobId, candidateId) {
    try {
      const payload = {
        jobId: jobId,
        candidateId: candidateId
      };

      const response = await this.client.post('/prod/interview-questions', payload);
      return response.data;
    } catch (error) {
      console.error('Error generating interview questions:', error);
      throw new Error(error.response?.data?.error || 'Failed to generate interview questions');
    }
  }

  // Health check
  async healthCheck() {
    try {
      await this.client.get('/prod/jobs');
      return { status: 'healthy', message: 'API accessible' };
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'error', message: 'API not accessible' };
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;
